import {Box, Skeleton, Table, TableBody, TableCell, TableHead, TableRow,} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {AxiosPromise} from "axios";
import AdminInner from "../admin_inner";
import AdminTitle from "../admin_title";
import {useApiManager} from "../../utils/api";
import {useLocation, useNavigate} from "react-router-dom";
import {FormButtonProps} from "../../form/form_inputs/form_button";
import ListButtons from "./list_buttons";

export interface AppPage<T> {
	content: T[];
	number: number;
	totalPages: number;
	size: number;
}

export interface AppListConfig<T> {
	title?: string;
	onClick: string | ((bean: T, idx?: number) => void);
	actions?: FormButtonProps[];
	headers?: (ListHeader | null)[];
	/** Filtros o resúmenes propios del listado, entre los botones y la tabla. */
	toolbar?: React.ReactNode;

	decorator(bean: T, idx: number): React.ReactNode;

	supplier(
		currentPage: number | undefined,
		size: number | undefined,
		term: string,
		showDismissed: boolean
	): AxiosPromise;
}

export interface ListHeader {
	title: string;
	align?: "left" | "right" | "center";
	width?: number | string;
}

interface AppListBodyProps<T> {
	config: AppListConfig<T>;
	trigger?: number;
	/**
	 * Filtros propios de la página (los del `toolbar`): al cambiar vuelve a pedir el listado desde la primera
	 * página, para no quedarse en una que con el filtro ya no existe. `trigger` recarga sin moverse de página.
	 */
	filterKey?: string;
	enabled?: boolean;
	searchable?: boolean;
	appendable?: boolean;
	paginable?: boolean;
	onAppend?: () => void;
}

export interface AppListConfProps<T> extends AppListBodyProps<T> {
	inner?: boolean;
}


const memory = new Map<string, any>();

function AppList<T>({
						config,
						trigger,
						filterKey,
						enabled = true,
						searchable = true,
						appendable = true,
						paginable = true,
						onAppend,
						inner = true,
					}: AppListConfProps<T>) {

	return (
		<>
			{inner && (
				<AdminInner>
					<AppListBody config={config}
								 trigger={trigger}
								 filterKey={filterKey}
								 searchable={searchable}
								 appendable={appendable}
								 paginable={paginable}
								 enabled={enabled}
								 onAppend={onAppend}/>
				</AdminInner>
			)}
			{!inner && (
				<AppListBody config={config}
							 trigger={trigger}
							 filterKey={filterKey}
							 searchable={searchable}
							 appendable={appendable}
							 paginable={paginable}
							 enabled={enabled}
							 onAppend={onAppend}/>
			)}
		</>

	);
}

export default AppList;


function AppListBody<T>({
							config,
							trigger,
							filterKey,
							enabled,
							searchable,
							appendable,
							paginable,
							onAppend,
						}: AppListBodyProps<T>) {

	const {decorator, title, supplier, onClick, actions} = config;

	const navigate = useNavigate();
	const location = useLocation();

	const apiManager = useApiManager();

	let initCurrentPage = 0;
	let initSize = 20;
	let initTerm = "";
	let initShowDismissed = false;
	if (memory.has(location.pathname)) {
		const params = memory.get(location.pathname);
		initCurrentPage = params.currentPage;
		initSize = params.size;
		initTerm = params.term;
		initShowDismissed = params.showDismissed;
	}

	const [contentFetch, setContentFetch] = useState<T[] | null>();
	const [totalPages, setTotalPages] = useState<number | undefined>(0);
	const [currentPage, setCurrentPage] = useState<number | undefined>(initCurrentPage);
	const [size, setSize] = useState<number | undefined>(initSize);
	const [term, setTerm] = useState<string>(initTerm);
	const [showDismissed, setShowDismissed] = useState<boolean>(initShowDismissed);


	const headers: ListHeader[] = (config.headers || []).filter(header => header != null) as ListHeader[];
	if (headers.length == 0) {
		const msg = "Convert grid to table is mandatory!";
		alert(msg);
		throw msg;
	}

	const lastFilterKey = useRef(filterKey);

	useEffect(() => {
		if (lastFilterKey.current !== filterKey) {
			lastFilterKey.current = filterKey;
			if (currentPage) {
				// Al volver a la primera página este efecto se repite y entonces pide el listado
				setCurrentPage(0);
				return;
			}
		}
		if (enabled) {
			setContentFetch(undefined);
			apiManager
			.execute(supplier(currentPage, size, term, showDismissed))
			.then((data: AppPage<T>) => {
				memory.set(location.pathname, {currentPage, size, term, showDismissed});
				setContentFetch(data.content);
				setTotalPages(data.totalPages);
			})
			.catch(() => {
				setContentFetch(null);
			});
		}
	}, [currentPage, showDismissed, term, size, trigger, enabled, filterKey]);

	const clickHandler = (event: any, value: any, idx: number) => {
		let newWindowReq = false;

		if (event.button == 1 || event.shiftKey || event.metaKey || event.altKey) {
			newWindowReq = true;
		}
		if (typeof onClick === "string") {
			const url = `${onClick}/${value["id"]}`;
			if (newWindowReq) {
				window.open(url, "_blank");
			} else {
				navigate(url);
			}
		} else if (typeof onClick === "function") {
			onClick(value, idx);
		} else {
			throw (
				"Type of detailsUrl is unknown " + JSON.stringify(onClick)
			);
		}
	}

	return (
		<>
			{title && <AdminTitle title={title}/>}

			{(paginable || searchable) &&
				<ListButtons
					onClick={onClick}
					currentPage={currentPage}
					setCurrentPage={setCurrentPage}
					totalPages={paginable ? totalPages : undefined}
					actions={actions}
					appendable={appendable}
					onAppend={onAppend}
					searchable={searchable}
					term={term}
					setTerm={(t) => { setCurrentPage(0); setTerm(t); }}
					showDismissed={showDismissed}
					setShowDismissed={(v) => { setCurrentPage(0); setShowDismissed(v); }}
				/>
			}

			{config.toolbar}

			{headers.length && (
				<Box sx={{borderRadius: 2.5, border: "1px solid", borderColor: "divider", overflowX: "auto", overflowY: "hidden",
					"& table": {border: 0}}}>
				{/*
				  El `table-layout: fixed` global reparte el ancho a partes iguales: en pantallas
				  estrechas las celdas con nowrap se salen y se pisan unas a otras. Por debajo de md la
				  tabla toma el ancho que necesita y se desplaza en horizontal dentro de esta caja; las
				  celdas se acotan para que una descripción larga no la estire sin límite.
				*/}
				<Table className={"table-list"}
					   sx={{
						   tableLayout: {xs: "auto", md: "fixed"},
						   minWidth: {xs: 600, md: 0},
						   "& td": {maxWidth: {xs: 260, md: "none"}},
					   }}>
					<TableHead>
						<TableRow>
							{headers.map((h, i) => (
								<TableCell
									width={h.width}
									key={"h-" + i}
									align={h.align}
									sx={{whiteSpace: "nowrap"}}
								>
									{h.title}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{contentFetch !== undefined && contentFetch !== null &&
							contentFetch.map((value: any, idx) =>
								<TableRow
									onClick={(event) => clickHandler(event, value, idx)}
									sx={{
										cursor: "pointer",
										opacity: value.dismissed ? 0.45 : 1,
										"&:hover": {backgroundColor: "action.hover"},
									}}
									key={idx + "list-item"}
									className={"table-list-item"}>
									{decorator(value, idx)}
								</TableRow>
							)}
						{contentFetch === undefined && [0, 1, 2, 3, 4].map(i => (
							<TableRow key={"sk-" + i}>
								{headers.map((_, j) => (
									<TableCell key={j}><Skeleton variant="text"/></TableCell>
								))}
							</TableRow>
						))}
						{contentFetch === null && (
							<TableRow>
								<TableCell colSpan={headers.length}
										   sx={{textAlign: "center", py: 4, color: "error.main"}}>
									No se ha podido conectar con el servidor
								</TableCell>
							</TableRow>
						)}
						{Array.isArray(contentFetch) && contentFetch.length === 0 && (
							<TableRow>
								<TableCell colSpan={headers.length}
										   sx={{textAlign: "center", py: 5, color: "text.secondary"}}>
									No hay registros
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				</Box>
			)}

		</>
	)
}